import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { Typography, Box, Paper, Tabs, Tab } from '@material-ui/core';
import ExtensionIcon from '@material-ui/icons/Extension';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { loadCentralWarehouses, loadCountries } from '../../store/country/countryActions';
import { getCurrentUser } from '../../util/host';
import { getCountryName } from './shared/helpers';
import {
  getApiBase,
  assertOkEnvelope,
  buildSlaRulesQuery,
  buildOptionOverridesQuery,
  resourceIdSegment,
  nonDynamicUrlHeaders,
  SLA_PATH_PATTERN,
} from './shared/slaApiHelpers';
import useSnackbar from './shared/useSnackbar';
import SnackbarAlert from './shared/SnackbarAlert';
import InfoBanner from './shared/InfoBanner';
import AttributeRuleForm from './AttributeRuleForm';
import AttributeRulesTable from './AttributeRulesTable';
import OptionRuleForm from './OptionRuleForm';
import OptionRulesTable from './OptionRulesTable';
import SlaRuleEditDialog from './SlaRuleEditDialog';

const StyledTab = withStyles(() => ({
  root: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '14px',
    minWidth: 180,
    '&:hover': { color: '#3f51b5', opacity: 1 },
  },
  selected: { color: '#3f51b5', fontWeight: 600 },
}))(Tab);

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: '#f5f5f5',
    minHeight: 'calc(100vh - 64px)',
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  titleIcon: {
    color: '#3f51b5',
    fontSize: 32,
    marginRight: theme.spacing(1),
  },
  title: {
    fontSize: '28px',
    fontWeight: 400,
    color: '#333',
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
  },
  tabsContainer: {
    backgroundColor: 'white',
    borderRadius: `${theme.spacing(1)}px ${theme.spacing(1)}px 0 0`,
    boxShadow: '0px 1px 4px rgba(0,0,0,0.06)',
    marginBottom: theme.spacing(3),
  },
  tabIndicator: {
    backgroundColor: '#3f51b5',
    height: 3,
  },
}));

const tabFromPath = (path) => (path.includes('option-overrides') ? 1 : 0);

const AdditionalSLA = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { snackbar, showSnack, closeSnack } = useSnackbar();

  const [activeTab, setActiveTab] = useState(() => tabFromPath(location.pathname));
  const [saving, setSaving] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editRule, setEditRule] = useState(null);

  const [ruleFilters, setRuleFilters] = useState({ statusFilter: 'true', warehouseFilter: '' });
  const [overrideFilters, setOverrideFilters] = useState({
    statusFilter: 'true',
    warehouseFilter: '',
    optionIdFilter: '',
    destinationCountryFilter: '',
  });

  const centralWarehouses = useSelector((state) => state.country.centralWarehouses);
  const countries = useSelector((state) => state.country.countries);

  const [attributeRules, setAttributeRules] = useState([]);
  const [optionRules, setOptionRules] = useState([]);
  const [loadingRules, setLoadingRules] = useState(true);
  const [loadingOverrides, setLoadingOverrides] = useState(true);

  const base = getApiBase();

  useEffect(() => {
    dispatch(loadCentralWarehouses());
    dispatch(loadCountries());
  }, [dispatch]);

  useEffect(() => {
    setActiveTab(tabFromPath(location.pathname));
  }, [location.pathname]);

  const fetchAttributeRules = useCallback(async () => {
    try {
      setLoadingRules(true);
      const q = buildSlaRulesQuery({
        statusFilter: ruleFilters.statusFilter === 'all' ? null : ruleFilters.statusFilter,
        warehouseFilter: ruleFilters.warehouseFilter || null,
      });
      const res = await axios.get(`${base}/api/sla/rules${q}`, {
        headers: nonDynamicUrlHeaders(SLA_PATH_PATTERN.RULES),
      });
      assertOkEnvelope(res);
      const list = res.data?.data?.rules;
      setAttributeRules(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
      showSnack(err.response?.data?.statusMsg || err.message || 'Failed to load SLA rules.', 'error');
      setAttributeRules([]);
    } finally {
      setLoadingRules(false);
    }
  }, [base, ruleFilters.statusFilter, ruleFilters.warehouseFilter, showSnack]);

  const fetchOptionOverrides = useCallback(async () => {
    try {
      setLoadingOverrides(true);
      const q = buildOptionOverridesQuery({
        statusFilter: overrideFilters.statusFilter === 'all' ? null : overrideFilters.statusFilter,
        warehouseFilter: overrideFilters.warehouseFilter || null,
        optionIdFilter: overrideFilters.optionIdFilter || null,
        destinationCountryFilter: overrideFilters.destinationCountryFilter || null,
      });
      const user = getCurrentUser();
      const token = user?.token != null ? String(user.token).trim() : '';
      const res = await axios.get(`${base}/api/sla/option-overrides${q}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...nonDynamicUrlHeaders(SLA_PATH_PATTERN.OPTION_OVERRIDES_LIST),
        },
      });
      assertOkEnvelope(res);
      const list = res.data?.data?.overrides;
      setOptionRules(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
      showSnack(err.response?.data?.statusMsg || err.message || 'Failed to load option overrides.', 'error');
      setOptionRules([]);
    } finally {
      setLoadingOverrides(false);
    }
  }, [
    base,
    overrideFilters.statusFilter,
    overrideFilters.warehouseFilter,
    overrideFilters.optionIdFilter,
    overrideFilters.destinationCountryFilter,
    showSnack,
  ]);

  useEffect(() => { fetchAttributeRules(); }, [fetchAttributeRules]);
  useEffect(() => { fetchOptionOverrides(); }, [fetchOptionOverrides]);

  const lookupCountry = useCallback(
    (code) => getCountryName(code, countries),
    [countries]
  );

  const handleTabChange = (_, t) => {
    setActiveTab(t);
    if (t === 0) {
      history.push('/sla/rules');
    } else {
      history.push('/sla/option-overrides');
    }
  };

  const handleCreateAttributeRule = async (payload) => {
    try {
      setSaving(true);
      const res = await axios.post(`${base}/api/sla/rules`, payload, {
        headers: {
          'Content-Type': 'application/json',
          ...nonDynamicUrlHeaders(SLA_PATH_PATTERN.RULES),
        },
      });
      assertOkEnvelope(res);
      showSnack(res.data?.statusMsg || 'SLA rule created successfully!');
      fetchAttributeRules();
      return true;
    } catch (err) {
      showSnack(err.response?.data?.statusMsg || err.message || 'Failed to create rule.', 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAttributeRule = async (ruleId) => {
    const idSeg = resourceIdSegment(ruleId);
    if (!idSeg) {
      showSnack('Missing rule id.', 'error');
      return;
    }
    try {
      const res = await axios.delete(`${base}/api/sla/rules/${idSeg}`, {
        headers: nonDynamicUrlHeaders(SLA_PATH_PATTERN.RULES_BY_ID),
      });
      assertOkEnvelope(res);
      showSnack(res.data?.statusMsg || 'Rule deleted.');
      fetchAttributeRules();
    } catch (err) {
      showSnack(err.response?.data?.statusMsg || err.message || 'Failed to delete rule.', 'error');
    }
  };

  const handleToggleRuleStatus = async (ruleId, status) => {
    const idSeg = resourceIdSegment(ruleId);
    if (!idSeg) {
      showSnack('Missing rule id.', 'error');
      return;
    }
    try {
      const res = await axios.post(`${base}/api/sla/rules/activate/${idSeg}`, { status }, {
        headers: {
          'Content-Type': 'application/json',
          ...nonDynamicUrlHeaders(SLA_PATH_PATTERN.RULES_ACTIVATE),
        },
      });
      assertOkEnvelope(res);
      showSnack(res.data?.statusMsg || (status ? 'Rule activated.' : 'Rule deactivated.'));
      fetchAttributeRules();
    } catch (err) {
      showSnack(err.response?.data?.statusMsg || err.message || 'Failed to update rule status.', 'error');
      fetchAttributeRules();
    }
  };

  const handleSaveEditedRule = async (id, payload) => {
    const idSeg = resourceIdSegment(id);
    if (!idSeg) {
      showSnack('Missing rule id.', 'error');
      return;
    }
    try {
      setEditSaving(true);
      const res = await axios.put(`${base}/api/sla/rules/${idSeg}`, payload, {
        headers: {
          'Content-Type': 'application/json',
          ...nonDynamicUrlHeaders(SLA_PATH_PATTERN.RULES_BY_ID),
        },
      });
      assertOkEnvelope(res);
      showSnack(res.data?.statusMsg || 'Rule updated.');
      setEditRule(null);
      fetchAttributeRules();
    } catch (err) {
      showSnack(err.response?.data?.statusMsg || err.message || 'Failed to update rule.', 'error');
    } finally {
      setEditSaving(false);
    }
  };

  const handleOptionUpload = async (formData) => {
    try {
      setSaving(true);
      const res = await axios.post(`${base}/api/sla/option-overrides/upload`, formData, {
        headers: nonDynamicUrlHeaders(SLA_PATH_PATTERN.OPTION_OVERRIDES_UPLOAD),
      });
      assertOkEnvelope(res);
      const count = res.data?.data?.option_count;
      const msg = count != null
        ? `${res.data?.statusMsg || 'Upload successful'} (${count} options).`
        : (res.data?.statusMsg || 'Upload successful.');
      showSnack(msg);
      fetchOptionOverrides();
      return true;
    } catch (err) {
      showSnack(err.response?.data?.statusMsg || err.message || 'Upload failed.', 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOptionOverride = async (id) => {
    const idSeg = resourceIdSegment(id);
    if (!idSeg) {
      showSnack('Missing override id.', 'error');
      return;
    }
    try {
      const res = await axios.delete(`${base}/api/sla/option-overrides/${idSeg}`, {
        headers: nonDynamicUrlHeaders(SLA_PATH_PATTERN.OPTION_OVERRIDES_BY_ID),
      });
      assertOkEnvelope(res);
      showSnack(res.data?.statusMsg || 'Override deleted.');
      fetchOptionOverrides();
    } catch (err) {
      showSnack(err.response?.data?.statusMsg || err.message || 'Failed to delete override.', 'error');
    }
  };

  const handleToggleOverrideStatus = async (id, status) => {
    const idSeg = resourceIdSegment(id);
    if (!idSeg) {
      showSnack('Missing override id.', 'error');
      return;
    }
    try {
      const res = await axios.put(`${base}/api/sla/option-overrides/${idSeg}`, { status }, {
        headers: {
          'Content-Type': 'application/json',
          ...nonDynamicUrlHeaders(SLA_PATH_PATTERN.OPTION_OVERRIDES_BY_ID),
        },
      });
      assertOkEnvelope(res);
      showSnack(res.data?.statusMsg || 'Override updated.');
      fetchOptionOverrides();
    } catch (err) {
      showSnack(err.response?.data?.statusMsg || err.message || 'Failed to update override.', 'error');
      fetchOptionOverrides();
    }
  };

  return (
    <div className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.titleContainer}>
          <ExtensionIcon className={classes.titleIcon} />
          <Typography className={classes.title}>SLA settings</Typography>
        </Box>
        <Typography className={classes.subtitle}>
          SLA rules and option overrides (APIs: /api/sla/rules, /api/sla/option-overrides)
        </Typography>
      </Box>

      <InfoBanner>
        <strong>SLA rules</strong> add extra days to last-mile ETA when attributes match (evaluated in search-city).
        <strong> Option overrides</strong> fine-tune specific option IDs via CSV upload.
        Fast delivery exclusions live under <strong>Fast Delivery Exclusions</strong> in the sidebar.
      </InfoBanner>

      <Paper className={classes.tabsContainer} elevation={0}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          classes={{ indicator: classes.tabIndicator }}
          variant="fullWidth"
        >
          <StyledTab label="SLA rules" />
          <StyledTab label="Option overrides" />
        </Tabs>
      </Paper>

      {activeTab === 0 && (
        <>
          <AttributeRuleForm
            warehouses={centralWarehouses}
            countries={countries}
            saving={saving}
            onSubmit={handleCreateAttributeRule}
            getCountryName={lookupCountry}
          />
          <AttributeRulesTable
            rules={attributeRules}
            loading={loadingRules}
            warehouses={centralWarehouses}
            countries={countries}
            statusFilter={ruleFilters.statusFilter}
            warehouseFilter={ruleFilters.warehouseFilter}
            onFiltersChange={(patch) => setRuleFilters((f) => ({ ...f, ...patch }))}
            onDelete={handleDeleteAttributeRule}
            onRefresh={fetchAttributeRules}
            onToggleStatus={handleToggleRuleStatus}
            onEdit={setEditRule}
          />
        </>
      )}

      {activeTab === 1 && (
        <>
          <OptionRuleForm
            warehouses={centralWarehouses}
            countries={countries}
            saving={saving}
            onUpload={handleOptionUpload}
            getCountryName={lookupCountry}
            showSnack={showSnack}
          />
          <OptionRulesTable
            rules={optionRules}
            loading={loadingOverrides}
            warehouses={centralWarehouses}
            countries={countries}
            statusFilter={overrideFilters.statusFilter}
            warehouseFilter={overrideFilters.warehouseFilter}
            optionIdFilter={overrideFilters.optionIdFilter}
            destinationCountryFilter={overrideFilters.destinationCountryFilter}
            onFiltersChange={(patch) => setOverrideFilters((f) => ({ ...f, ...patch }))}
            onDelete={handleDeleteOptionOverride}
            onRefresh={fetchOptionOverrides}
            onToggleStatus={handleToggleOverrideStatus}
          />
        </>
      )}

      <SlaRuleEditDialog
        open={Boolean(editRule)}
        rule={editRule}
        onClose={() => setEditRule(null)}
        onSave={handleSaveEditedRule}
        saving={editSaving}
        warehouses={centralWarehouses}
        countries={countries}
        getCountryName={lookupCountry}
      />

      <SnackbarAlert open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={closeSnack} />
    </div>
  );
};

export default AdditionalSLA;
